import { useEffect, useRef, useState } from "react";
import { CheckCircle, WarningCircle, Warning, CloudArrowUp, Paperclip, ArrowClockwise, X } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useApi, errMsg, useWorkspace } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const STATUS = {
  green: { cls: "bg-green-50 text-green-800 border-green-200", dot: "bg-green-500", text: "Current", Icon: CheckCircle },
  orange: { cls: "bg-orange-50 text-orange-800 border-orange-200", dot: "bg-orange-500", text: "Due soon", Icon: Warning },
  red: { cls: "bg-red-50 text-red-800 border-red-200", dot: "bg-red-500", text: "Action required", Icon: WarningCircle },
};

export const StatusBadge = ({ level, onClick, testid, compact }) => {
  const s = STATUS[level] || STATUS.green;
  const Comp = onClick ? "button" : "span";
  return (
    <Comp onClick={onClick} data-testid={testid} className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-semibold ${s.cls} ${onClick ? "hover:brightness-95" : ""}`}>
      <s.Icon size={14} weight="fill" />{compact ? s.text.split(" ")[0] : s.text}
    </Comp>
  );
};

export const Pill = ({ children, tone = "slate", testid }) => {
  const tones = { slate: "bg-slate-100 text-slate-700", green: "bg-green-50 text-green-800", orange: "bg-orange-50 text-orange-800",
    red: "bg-red-50 text-red-800", blue: "bg-blue-50 text-blue-800", navy: "bg-slate-900 text-white" };
  return <span data-testid={testid} className={`inline-flex items-center rounded px-2 py-0.5 text-xs font-medium ${tones[tone]}`}>{children}</span>;
};

export const PageHeader = ({ title, subtitle, actions }) => (
  <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between mb-8 fade-in">
    <div>
      <h1 className="text-3xl sm:text-4xl font-bold tracking-tight" data-testid="page-title">{title}</h1>
      {subtitle && <p className="mt-2 text-sm text-slate-600 max-w-2xl">{subtitle}</p>}
    </div>
    {actions && <div className="flex flex-wrap gap-2">{actions}</div>}
  </div>
);

export const Section = ({ title, children, actions, testid }) => (
  <section className="panel p-5 sm:p-6 fade-in" data-testid={testid}>
    {(title || actions) && (
      <div className="flex items-center justify-between gap-3 mb-4">
        <h2 className="text-base md:text-lg font-semibold">{title}</h2>
        {actions}
      </div>
    )}
    {children}
  </section>
);

export const Empty = ({ children, testid }) => <p data-testid={testid} className="text-sm text-slate-500 py-6">{children}</p>;

export const Field = ({ label: l, children, hint }) => (
  <label className="block space-y-1.5">
    <span className="label-sm">{l}</span>
    {children}
    {hint && <span className="block text-xs text-slate-500">{hint}</span>}
  </label>
);

export const Btn = ({ variant = "primary", className = "", children, ...p }) => {
  const v = { primary: "bg-slate-900 text-white hover:bg-slate-800", outline: "border border-slate-300 bg-white text-slate-900 hover:bg-slate-50",
    ghost: "text-slate-700 hover:bg-slate-100", danger: "bg-red-600 text-white hover:bg-red-700", accent: "text-white hover:brightness-110" };
  return (
    <button {...p} style={variant === "accent" ? { background: "var(--group-accent)" } : undefined}
      className={`inline-flex items-center justify-center gap-2 h-11 px-5 rounded-md text-sm font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${v[variant]} ${className}`}>
      {children}
    </button>
  );
};

export const Modal = ({ open, onClose, title, children, wide, testid }) => (
  <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
    <DialogContent data-testid={testid} className={`bg-white max-h-[90vh] overflow-y-auto ${wide ? "sm:max-w-3xl" : "sm:max-w-lg"}`}>
      <DialogHeader><DialogTitle className="font-display">{title}</DialogTitle></DialogHeader>
      {children}
    </DialogContent>
  </Dialog>
);

export const SaveState = ({ state, onRetry }) => {
  if (state === "saving") return <span data-testid="save-state" className="text-xs text-slate-500">Saving…</span>;
  if (state === "saved") return <span data-testid="save-state" className="text-xs text-green-700 inline-flex items-center gap-1"><CheckCircle size={14} />Saved</span>;
  if (state === "failed") return (
    <button data-testid="save-retry" onClick={onRetry} className="text-xs text-red-700 inline-flex items-center gap-1 font-semibold">
      <ArrowClockwise size={14} />Save failed — retry
    </button>);
  if (state === "dirty") return <span data-testid="save-state" className="text-xs text-orange-700">Unsaved changes</span>;
  return null;
};

export const useUnsavedGuard = (dirty) => {
  useEffect(() => {
    const h = (e) => { if (dirty) { e.preventDefault(); e.returnValue = ""; } };
    window.addEventListener("beforeunload", h);
    return () => window.removeEventListener("beforeunload", h);
  }, [dirty]);
};

export function FileUpload({ farmId, category = "general", value = [], onChange, testid = "file-upload" }) {
  const api = useApi();
  const ref = useRef();
  const [busy, setBusy] = useState(null);
  const [failed, setFailed] = useState(null);
  const upload = async (file) => {
    setBusy(file.name); setFailed(null);
    try {
      const fd = new FormData();
      fd.append("farm_id", farmId); fd.append("category", category); fd.append("file", file);
      const rec = await api.post("/files", fd, { onUploadProgress: (e) => setBusy(`${file.name} ${Math.round((e.loaded / (e.total || 1)) * 100)}%`) });
      onChange([...value, rec.id]);
      toast.success(`Uploaded ${file.name}`);
    } catch (e) {
      setFailed({ file, msg: errMsg(e) });
    } finally { setBusy(null); }
  };
  return (
    <div className="space-y-2">
      <input ref={ref} type="file" className="hidden" accept="image/*,application/pdf,.csv,.docx,.xlsx" data-testid={`${testid}-input`}
        onChange={(e) => { const f = e.target.files?.[0]; if (f) upload(f); e.target.value = ""; }} />
      <Btn type="button" variant="outline" onClick={() => ref.current.click()} disabled={!!busy} data-testid={testid}>
        <CloudArrowUp size={18} />{busy ? `Uploading ${busy}` : "Add photo / file"}
      </Btn>
      {failed && (
        <p className="text-xs text-red-700" data-testid={`${testid}-error`}>Upload failed: {failed.msg}{" "}
          <button className="underline font-semibold" onClick={() => upload(failed.file)}>Retry</button></p>)}
      <Attachments ids={value} onRemove={(id) => onChange(value.filter((x) => x !== id))} />
    </div>
  );
}

export function Attachments({ ids = [], onRemove }) {
  const api = useApi();
  const [meta, setMeta] = useState([]);
  const key = ids.join(",");
  useEffect(() => {
    if (!ids.length) { setMeta([]); return; }
    api.post("/files/meta", { ids }).then(setMeta).catch(() => setMeta([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, api]);
  const open = async (m) => {
    try {
      const b = await api.blob(`/files/${m.id}`);
      window.open(URL.createObjectURL(b), "_blank");
    } catch (e) { toast.error(errMsg(e)); }
  };
  if (!meta.length) return null;
  return (
    <ul className="flex flex-wrap gap-2">
      {meta.map((m) => (
        <li key={m.id} className="inline-flex items-center gap-1 rounded border border-slate-200 bg-slate-50 px-2 py-1 text-xs">
          <button type="button" onClick={() => open(m)} className="inline-flex items-center gap-1 hover:underline" data-testid={`attachment-${m.id}`}>
            <Paperclip size={12} />{m.filename}
          </button>
          {onRemove && <button type="button" onClick={() => onRemove(m.id)} aria-label="Remove"><X size={12} /></button>}
        </li>
      ))}
    </ul>
  );
}

export const FarmRequired = ({ children, module }) => {
  const { farm, farms, setFarmId, mods } = useWorkspace();
  if (!farm) return (
    <div className="panel p-8 max-w-xl" data-testid="farm-required">
      <h2 className="text-lg font-semibold mb-2">Choose a farm</h2>
      <p className="text-sm text-slate-600 mb-4">This area works one farm at a time.</p>
      {farms.length ? (
        <div className="flex flex-wrap gap-2">{farms.map((f) => <Btn key={f.id} variant="outline" onClick={() => setFarmId(f.id)} data-testid={`choose-farm-${f.id}`}>{f.name}</Btn>)}</div>
      ) : <p className="text-sm text-slate-500">No farms are assigned to you yet. An administrator can add farms in Settings.</p>}
    </div>);
  if (module && mods && !mods[module]) return <div className="panel p-8 max-w-xl" data-testid="module-disabled"><h2 className="text-lg font-semibold">This module is switched off for {farm.name}</h2><p className="text-sm text-slate-600 mt-2">{module === "feeding" && farm.halter ? "Halter farms do not use the feed-sheet module." : "An administrator can enable it in Settings. Historical records are kept."}</p></div>;
  return children;
};

export const Sel = ({ value, onChange, options, testid, placeholder, className = "" }) => (
  <select data-testid={testid} value={value ?? ""} onChange={(e) => onChange(e.target.value)} className={`field-input ${className}`}>
    {placeholder !== undefined && <option value="">{placeholder}</option>}
    {options.map((o) => (typeof o === "string" ? <option key={o} value={o}>{o}</option> : <option key={o.value} value={o.value}>{o.label}</option>))}
  </select>
);

export const Bar = ({ pct, testid }) => {
  const w = Math.min(pct ?? 0, 100);
  const over = (pct ?? 0) > 100;
  return (
    <div className="h-2.5 w-full rounded-full bg-slate-100 overflow-hidden" data-testid={testid}>
      <div className={`h-full rounded-full ${over ? "bg-red-500" : "bg-slate-800"}`} style={{ width: `${w}%`, transition: "width .4s ease" }} />
    </div>
  );
};

export const useLoad = (fn, deps) => {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [tick, setTick] = useState(0);
  useEffect(() => {
    let live = true;
    setErr(null);
    fn().then((d) => live && setData(d)).catch((e) => live && setErr(errMsg(e)));
    return () => { live = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, tick]);
  return [data, () => setTick((t) => t + 1), err, setData];
};
